/**
 * Frequently Bought Together - Advanced JavaScript functionality
 * Handles product selection, price calculation, and cart operations
 */

// Prevent redeclaration error
if (typeof window.FrequentlyBoughtTogether === 'undefined') {
  window.FrequentlyBoughtTogether = class FrequentlyBoughtTogether {
  constructor(container) {
    this.container = container;
    this.products = container.querySelectorAll('.fbt-product-item');
    this.checkboxes = container.querySelectorAll('.fbt-checkbox');
    this.variantSelects = container.querySelectorAll('.fbt-variant-select');
    this.totalPriceElement = container.querySelector('[data-total-price]');
    this.discountSection = container.querySelector('[data-discount-section]');
    this.discountAmountEl = container.querySelector('[data-discount-amount]');
    this.finalTotalSection = container.querySelector('[data-final-total]');
    this.finalTotalPrice = container.querySelector('[data-final-total-price]');
    this.addToCartBtn = container.querySelector('[data-add-selected-to-cart]');
    this.btnText = this.addToCartBtn?.querySelector('.fbt-btn-text');
    this.btnLoading = this.addToCartBtn?.querySelector('.fbt-btn-loading');
    
    // Get settings from data attributes or defaults
    this.enableDiscount = this.container.dataset.enableDiscount === 'true';
    this.discountPercentage = parseFloat(this.container.dataset.discountPercentage) || 0;
    // numeric value (cents) – keep element reference separate
    this.discountValue = parseFloat(this.container.dataset.discountAmount) || 0;
    this.currency = window.Shopify?.currency?.active || 'USD';
    // Seed from URL ?variant=ID if present (Shopify pattern)
    this.mainVariantId = this.getVariantIdFromUrl() || this.getCurrentVariantId();
    this.mainVariantTitle = this.getMainVariantTitle();
    this.mainVariantSize = this.getMainVariantSizeToken(this.mainVariantTitle);
    // If URL variant provided but title/size not yet resolved, derive from product JSON
    if (this.mainVariantId && (!this.mainVariantTitle || !this.mainVariantSize)) {
      const resolvedTitle = this.resolveVariantTitleById(this.mainVariantId);
      if (resolvedTitle) {
        this.mainVariantTitle = this.mainVariantTitle || resolvedTitle;
        this.mainVariantSize = this.mainVariantSize || this.getMainVariantSizeToken(resolvedTitle);
      }
    }
    
    this.init();
  }
  
  init() {
    // Check if AJAX fetch is enabled
    if (this.container.dataset.fetchAllAjax === 'true') {
      this.fetchAllProductsAjax();
    } else {
      this.bindEvents();
      this.selectAllProductsByDefault();
      this.syncWithCurrentMainVariant();
      this.updateTotal();
      this.initializeAnalytics();
      this.listenToMainProductVariantChanges();
      // Safety: re-sync once more on next tick (handles late DOM/analytics availability)
      setTimeout(() => this.syncWithCurrentMainVariant(), 0);
      setTimeout(() => this.syncWithCurrentMainVariant(), 120);
      // Safety: re-sync once more on next tick (handles late DOM/analytics availability)
      setTimeout(() => this.syncWithCurrentMainVariant(), 0);
      setTimeout(() => this.syncWithCurrentMainVariant(), 120);
    }
  }
  
  async fetchAllProductsAjax() {
    const collectionHandle = this.container.dataset.collectionHandle;
    const collectionId = this.container.dataset.collectionId;
    const collectionGid = this.container.dataset.collectionGid;
    const storefrontApiToken = this.container.dataset.storefrontApiToken;
    const shopDomain = this.container.dataset.shopDomain;
    const demo = this.container.dataset.demo;
    const currentProductId = this.container.dataset.currentProductId;
    const currentProductHandle = this.container.dataset.currentProductHandle;
    const currentVendor = this.container.dataset.currentVendor;
    const currentColorKey = this.container.dataset.currentColorKey;
    const currentTypeList = this.container.dataset.currentTypeList ? this.container.dataset.currentTypeList.split(',') : [];
    const currentType = this.container.dataset.currentType;
    // Default to true unless explicitly set to "false" (safer when attribute is missing)
    const includeOriginalProduct = this.container.dataset.includeOriginalProduct !== 'false';
    // maxProducts from data attribute is already adjusted by Liquid (reduced by 1 if includeOriginalProduct is true)
    // So we use it directly - no need to reduce again
    const maxProducts = parseInt(this.container.dataset.maxProducts) || 3;
    
    if (!collectionHandle) {
      console.error('FBT: Collection handle not found');
      return;
    }
    
    // Show loading state
    const loadingEl = this.container.querySelector('[data-fbt-loading]');
    if (loadingEl) loadingEl.style.display = 'block';
    
    const productsContainer = this.container.querySelector('[data-fbt-products]');
    if (productsContainer) {
      // Hide placeholder if exists
      const placeholder = productsContainer.querySelector('.fbt-ajax-placeholder');
      if (placeholder) placeholder.style.display = 'none';
      // Ensure flex display for horizontal layout (CSS expects flex)
      productsContainer.style.display = 'flex';
    }
    
    try {
      let allProducts = [];
      
      // Use Storefront API if token is provided
      if (storefrontApiToken && shopDomain) {
        // Try GID first, fallback to handle
        const collectionIdentifier = collectionGid || collectionHandle;
        
        // Process incrementally - start after first batch
        let processedEarly = false;
        const processEarly = async (productsSoFar) => {
          // After first chunk, check if we already have enough matches and stop fetching early
          const threshold = Math.max(100, maxProducts * 10);
          if (!processedEarly && productsSoFar.length >= threshold) {
            processedEarly = true;
            const filteredEarly = includeOriginalProduct 
              ? productsSoFar 
              : productsSoFar.filter(p => p.id.toString() !== currentProductId);
            if (filteredEarly.length > 0) {
              const earlySelected = this.processProductsForFBT(
                filteredEarly,
                { demo, currentVendor, currentColorKey, currentTypeList, currentType, maxProducts, includeOriginalProduct, currentProductId, currentProductHandle }
              );
              
              if (earlySelected.length >= maxProducts) {
                return true; // signal early stop
            }
          }
          }
          return false;
        };
        
        allProducts = await this.fetchCollectionProductsGraphQL(
          shopDomain, 
          storefrontApiToken, 
          collectionIdentifier, 
          collectionGid ? 'gid' : 'handle',
          processEarly,
          maxProducts,
          includeOriginalProduct,
          currentProductId
        );
        
        // Don't skip final processing - always process all products for best results
      } else {
        // Fallback to HTML parsing
        allProducts = await this.fetchCollectionProducts(collectionHandle);
      }
      
      if (allProducts.length === 0) {
        console.warn('FBT: No products fetched. This might be due to:');
        console.warn('1. Collection page format not matching expected structure');
        console.warn('2. Products not accessible via AJAX');
        console.warn('3. Network/timeout issues');
        console.warn('Consider using a custom JSON endpoint or Storefront API instead.');
      }
      
      if (allProducts.length === 0) {
        console.warn('FBT: No products fetched from collection');
        if (loadingEl) {
          loadingEl.innerHTML = '<p>No products found in collection.</p>';
        }
        return;
      }
      
      // Conditionally filter out current product
      const filteredProducts = includeOriginalProduct 
        ? allProducts 
        : allProducts.filter(p => p.id.toString() !== currentProductId);
      
      const selectedProducts = this.processProductsForFBT(
        filteredProducts,
        {
          demo,
          currentVendor,
          currentColorKey,
          currentTypeList,
          currentType,
          maxProducts,
          includeOriginalProduct,
          currentProductId,
          currentProductHandle
        }
      );
      
      console.log('FBT: Selected products:', selectedProducts.length, 'Expected:', maxProducts, selectedProducts);

      // Store rendered products early so getPreferredVariant can access them
      this.renderedProducts = selectedProducts;
      
      // Ensure main variant title/size are resolved from fetched products (when URL variant provided)
      this.ensureMainVariantMetadata(selectedProducts);
      
      // Debug: log main variant info after ensuring metadata
      console.log('FBT: Main variant after ensureMainVariantMetadata:', {
        mainVariantId: this.mainVariantId,
        mainVariantTitle: this.mainVariantTitle,
        mainVariantSize: this.mainVariantSize
      });
      
      // If we need to include the current product but it's not in the collection, try to get it from the page
      if (includeOriginalProduct && currentProductId && !selectedProducts.find(p => p.id.toString() === currentProductId.toString())) {
        console.log('FBT: Current product not in collection, attempting to fetch from page...');
        // Try to get current product data from page context
        const currentProductData = this.getCurrentProductFromPage() || this.getCurrentProductFromAnalytics();
        if (currentProductData) {
          selectedProducts.unshift(currentProductData);
          console.log('FBT: Current product added from page context');
        } else {
          console.warn('FBT: Could not find current product data on page');
        }
      }
      
      // Warn if we got fewer products than expected
      if (selectedProducts.length < maxProducts) {
        console.warn(`FBT: Only found ${selectedProducts.length} products, expected ${maxProducts}. This might be due to insufficient products in collection or strict matching criteria.`);
      }
      
      if (selectedProducts.length === 0) {
        console.warn('FBT: No products selected after processing');
        if (loadingEl) {
          loadingEl.innerHTML = '<p>No matching products found.</p>';
        }
        return;
      }
      
      // Render products using GraphQL data directly (much faster)
      this.renderProductsFromData(selectedProducts);
      
      // Hide loading, show products
      if (loadingEl) loadingEl.style.display = 'none';
      if (productsContainer) productsContainer.style.display = 'flex';
      
      // Initialize FBT functionality
      this.bindEvents();
      this.selectAllProductsByDefault();
      this.syncWithCurrentMainVariant();
      this.updateTotal();
      this.initializeAnalytics();
      this.listenToMainProductVariantChanges();
      
    } catch (error) {
      console.error('FBT: Error fetching products', error);
      console.error('FBT: Error stack:', error.stack);
      if (loadingEl) {
        loadingEl.innerHTML = '<p>Error loading recommendations. Please refresh the page.</p>';
      }
    }
  }
  
  async fetchCollectionProductsGraphQL(shopDomain, apiToken, collectionIdentifier, identifierType = 'gid', onProgress = null, maxProducts = 3, includeOriginalProduct = false, currentProductId = null) {
    const allProducts = [];
    let cursor = null;
    let hasNextPage = true;
    const maxRequests = 12; // cap to avoid long fetch cycles
    let requestCount = 0;
    // Dynamic early stop threshold - fetch enough to find good matches
    // Keep lower for speed on huge collections
    const earlyStopThreshold = Math.max(200, maxProducts * 50);
    
    const graphqlEndpoint = `https://${shopDomain}/api/2024-01/graphql.json`;
    
    // Determine query type based on identifier
    const useHandle = identifierType === 'handle';
    const queryName = useHandle ? 'getCollectionByHandle' : 'getCollectionProducts';
    const queryVariable = useHandle ? '$handle: String!' : '$id: ID!';
    const collectionField = useHandle ? 'collectionByHandle(handle: $handle)' : 'collection(id: $id)';
    
    while (hasNextPage && requestCount < maxRequests) {
      try {
        const query = `
          query ${queryName}(${queryVariable}, $first: Int!, $after: String) {
            ${collectionField} {
              products(first: $first, after: $after) {
                pageInfo {
                  hasNextPage
                  endCursor
                }
                edges {
                  node {
                    id
                    handle
                    title
                    vendor
                    productType
                    tags
                    featuredImage {
                      url
                      altText
                    }
                    priceRange {
                      minVariantPrice {
                        amount
                        currencyCode
                      }
                    }
                    variants(first: 10) {
                      edges {
                        node {
                          id
                          title
                          availableForSale
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                        }
                      }
                    }
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                    metafields(identifiers: [
                      {namespace: "custom", key: "color"},
                      {namespace: "custom", key: "product_type_filter"}
                    ]) {
                      id
                      namespace
                      key
                      value
                      type
                    }
                  }
                }
              }
            }
          }
        `;
        
        // Use maximum batch size (250 is Shopify's limit for products)
        const batchSize = 250;
        const variables = useHandle 
          ? { handle: collectionIdentifier, first: batchSize, after: cursor }
          : { id: collectionIdentifier, first: batchSize, after: cursor };
        
        console.log(`FBT: GraphQL request ${requestCount + 1}, cursor: ${cursor || 'null'}`);
        
        const response = await fetch(graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': apiToken
          },
          body: JSON.stringify({ query, variables })
        });
        
        if (!response.ok) {
          console.error(`FBT: GraphQL request failed: ${response.status}`, await response.text());
          break;
        }
        
        const result = await response.json();
        
        if (result.errors) {
          console.error('FBT: GraphQL errors:', result.errors);
          break;
        }
        
        const products = result.data?.collection?.products;
        if (!products) {
          console.warn('FBT: No products in GraphQL response');
          break;
        }
        
        // Process products
        products.edges.forEach(edge => {
          const node = edge.node;
          
          // Parse metafields
          const colorMetafield = node.metafields?.find(m => m.key === 'color');
          const typeMetafield = node.metafields?.find(m => m.key === 'product_type_filter');
          
          let colorValues = [];
          let typeValues = [];
          
          if (colorMetafield) {
            if (colorMetafield.type === 'list.single_line_text_field') {
              try {
                colorValues = JSON.parse(colorMetafield.value || '[]');
              } catch (e) {
                colorValues = colorMetafield.value ? [colorMetafield.value] : [];
              }
            } else {
              colorValues = colorMetafield.value ? [colorMetafield.value] : [];
            }
          }
          
          if (typeMetafield) {
            if (typeMetafield.type === 'list.single_line_text_field') {
              try {
                typeValues = JSON.parse(typeMetafield.value || '[]');
              } catch (e) {
                typeValues = typeMetafield.value ? [typeMetafield.value] : [];
              }
            } else {
              typeValues = typeMetafield.value ? [typeMetafield.value] : [];
            }
          }
          
          // Extract product ID (remove gid://shopify/Product/ prefix)
          const productId = node.id.replace('gid://shopify/Product/', '');
          
          // Get images - GraphQL returns full URLs (usually https://)
          const images = node.images?.edges?.map(e => {
            let url = e.node.url;
            // Ensure URL is complete (handle protocol-relative URLs)
            if (url && url.startsWith('//')) {
              url = `https:${url}`;
            } else if (url && !url.startsWith('http')) {
              url = `https://${url}`;
            }
            return url;
          }) || [];
          
          let featuredImage = node.featuredImage?.url || images[0] || '';
          // Ensure featured image URL is complete
          if (featuredImage) {
            if (featuredImage.startsWith('//')) {
              featuredImage = `https:${featuredImage}`;
            } else if (!featuredImage.startsWith('http')) {
              featuredImage = `https://${featuredImage}`;
            }
          }
          
          allProducts.push({
            id: productId,
            handle: node.handle,
            title: node.title,
            vendor: node.vendor || '',
            type: node.productType || '',
            price: this.toCents(node.priceRange.minVariantPrice.amount),
            image: featuredImage,
            images: images.map(img => img.startsWith('http') ? img : `https:${img}`),
            featured_image: featuredImage,
            variants: node.variants.edges.map(e => ({
              id: e.node.id.replace('gid://shopify/ProductVariant/', ''),
              title: e.node.title,
              available: e.node.availableForSale,
              price: this.toCents(e.node.price.amount),
              compare_at_price: e.node.compareAtPrice ? this.toCents(e.node.compareAtPrice.amount) : 0
            })),
            metafields: {
              color: colorValues,
              product_type_filter: typeValues
            },
            tags: node.tags || []
          });
        });
        
        console.log(`FBT: GraphQL fetched ${products.edges.length} products (total: ${allProducts.length})`);
        
        // Call progress callback for early processing
        let shouldStop = false;
        if (onProgress && typeof onProgress === 'function') {
          try {
            shouldStop = await onProgress(allProducts.slice()); // Pass a copy
            if (shouldStop === true) {
              console.log('FBT: Early processing found enough matches, stopping fetch');
              hasNextPage = false;
            }
          } catch (e) {
            console.warn('FBT: Error in progress callback:', e);
          }
        }
        
        // Check if there are more pages
        hasNextPage = products.pageInfo.hasNextPage && !shouldStop;
        cursor = products.pageInfo.endCursor;
        requestCount++;
        
        // Early stopping: If we have enough products, we can stop fetching
        // For large collections, we don't need to fetch everything - just enough to find good matches
        if (!shouldStop && allProducts.length >= earlyStopThreshold) {
          console.log(`FBT: Reached ${allProducts.length} products (threshold: ${earlyStopThreshold}), stopping early for performance`);
          hasNextPage = false;
        }
        
        // Hard limit: don't fetch more than 1000 products (Shopify's practical limit per page)
        if (!shouldStop && allProducts.length >= 1000) {
          console.log(`FBT: Reached 1000 products limit, stopping`);
          hasNextPage = false;
        }
        
        // No delay - fetch as fast as possible (Shopify can handle it)
        
      } catch (error) {
        console.error(`FBT: GraphQL request error:`, error);
        break;
      }
    }
    
    console.log(`FBT: GraphQL fetch complete. Total products: ${allProducts.length}`);
    return allProducts;
  }
  
  async fetchCollectionProducts(collectionHandle) {
    console.log('FBT: fetchCollectionProducts called for:', collectionHandle);
    const allProducts = [];
    let page = 1;
    let hasMore = true;
    const maxPages = 100; // Safety limit
    
    while (hasMore && page <= maxPages) {
      try {
        console.log(`FBT: Fetching page ${page}...`);
        // Fetch collection page
        const response = await fetch(`/collections/${collectionHandle}?page=${page}`);
        if (!response.ok) {
          console.warn(`FBT: Response not OK for page ${page}:`, response.status);
          break;
        }
        
        const html = await response.text();
        console.log(`FBT: Page ${page} HTML length:`, html.length);
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Try to find product data from grid items (most reliable method)
        const productGridItems = doc.querySelectorAll('[data-product-id][data-product-handle]');
        console.log(`FBT: Page ${page} - Found ${productGridItems.length} product grid items`);
        
        if (productGridItems.length > 0) {
          // Extract product data from grid items
          productGridItems.forEach(item => {
            const productId = item.dataset.productId;
            const productHandle = item.dataset.productHandle;
            
            // Extract additional data from the item
            const titleEl = item.querySelector('.grid-product__title, .product-title, h3, h2, [data-product-title]');
            const title = titleEl ? titleEl.textContent.trim() : '';
            
            const vendorEl = item.querySelector('.grid-product__vendor, .product-vendor, [data-product-vendor]');
            const vendor = vendorEl ? vendorEl.textContent.trim() : '';
            
            const typeEl = item.querySelector('[data-product-type]');
            const type = typeEl ? typeEl.textContent.trim() : '';
            
            const priceEl = item.querySelector('.grid-product__price, .product-price, [data-product-price]');
            const rawPrice = priceEl ? (priceEl.dataset.price || priceEl.textContent.replace(/[^0-9\.,]/g, '')) : 0;
            const price = this.toCents(rawPrice);
            
            const imageEl = item.querySelector('img');
            const image = imageEl ? imageEl.src : '';
            
            // Try to get metafields from data attributes
            const colorMeta = item.dataset.productColor ? item.dataset.productColor.split(',').filter(c => c.trim()) : [];
            const typeMeta = item.dataset.productTypeFilter ? item.dataset.productTypeFilter.split(',').filter(t => t.trim()) : [];
            
            if (productId && productHandle) {
              allProducts.push({
                id: productId.toString(),
                handle: productHandle,
                title: title,
                vendor: vendor,
                type: type,
                price: price,
                image: image,
                variants: [], // We'll need to fetch this separately if needed
                metafields: {
                  color: colorMeta,
                  product_type_filter: typeMeta
                },
                tags: []
              });
            }
          });
          
          console.log(`FBT: Page ${page} - Extracted ${productGridItems.length} products from grid items`);
        }
        
        // Fallback: Try to find product JSON data in script tags
        const productScripts = doc.querySelectorAll('script[type="application/json"][data-product-json]');
        if (productScripts.length > 0 && allProducts.length === 0) {
          let productsFromScripts = 0;
          productScripts.forEach(script => {
            try {
              const productData = JSON.parse(script.textContent);
              if (productData && productData.id) {
                allProducts.push({
                  id: productData.id.toString(),
                  handle: productData.handle,
                  title: productData.title,
                  vendor: productData.vendor || '',
                  type: productData.type || '',
                  price: productData.variants?.[0]?.price || 0,
                  image: productData.featured_image || productData.images?.[0] || '',
                  variants: productData.variants || [],
                  metafields: {
                    color: productData.metafields?.custom?.color || [],
                    product_type_filter: productData.metafields?.custom?.product_type_filter || []
                  },
                  tags: productData.tags || []
                });
                productsFromScripts++;
              }
            } catch (e) {
              console.error('FBT: Error parsing product JSON', e);
            }
          });
          console.log(`FBT: Page ${page} - Extracted ${productsFromScripts} products from scripts`);
        }
        
        // Last resort: extract from links (very slow, only if nothing else works)
        const productLinks = doc.querySelectorAll('a[href*="/products/"]');
        if (allProducts.length === 0 && productLinks.length > 0) {
          console.log(`FBT: No grid items found, falling back to link extraction (slow method)...`);
          console.log(`FBT: No JSON scripts found, extracting from ${productLinks.length} product links...`);
          const handles = new Set();
          productLinks.forEach(link => {
            const match = link.href.match(/\/products\/([^\/\?#]+)/);
            if (match) handles.add(match[1]);
          });
          
          console.log(`FBT: Found ${handles.size} unique product handles`);
          
          // Fetch product data for each handle (limit to 20 per page to avoid timeout)
          // For large collections, consider using Storefront API or custom JSON endpoint
          const handlesArray = Array.from(handles).slice(0, 20);
          console.log(`FBT: Fetching product data for ${handlesArray.length} products (limited to avoid timeout)...`);
          console.log(`FBT: First few handles:`, handlesArray.slice(0, 5));
          
          let fetchedCount = 0;
          for (let i = 0; i < handlesArray.length; i++) {
            const handle = handlesArray[i];
            try {
              console.log(`FBT: Fetching product data for: ${handle}`);
              const productData = await this.fetchProductData(handle);
              if (productData && productData.id) {
                allProducts.push({
                  id: productData.id.toString(),
                  handle: productData.handle,
                  title: productData.title,
                  vendor: productData.vendor || '',
                  type: productData.type || '',
                  price: productData.variants?.[0]?.price || 0,
                  image: productData.featured_image || productData.images?.[0] || '',
                  variants: productData.variants || [],
                  metafields: {
                    color: productData.metafields?.custom?.color || [],
                    product_type_filter: productData.metafields?.custom?.product_type_filter || []
                  },
                  tags: productData.tags || []
                });
                fetchedCount++;
                console.log(`FBT: [${i+1}/${handlesArray.length}] Successfully fetched: ${productData.title} (${fetchedCount} total)`);
              } else {
                console.warn(`FBT: [${i+1}/${handlesArray.length}] No product data returned for handle: ${handle}`);
              }
              // Small delay to avoid rate limiting (increased for stability)
              if (i < handlesArray.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 100));
              }
            } catch (e) {
              console.error(`FBT: Error fetching product ${handle}`, e);
            }
          }
          
          console.log(`FBT: Page ${page} - Fetched ${fetchedCount} products from links`);
          
          // If we got fewer than 50 products, we're probably on the last page
          if (fetchedCount < 50) {
            hasMore = false;
          }
        }
        
        // Check if there are more pages
        console.log(`FBT: Page ${page} - Total products so far: ${allProducts.length}`);
        
        if (allProducts.length === 0) {
          console.log(`FBT: No products found on page ${page}, stopping`);
          hasMore = false;
        } else if (productGridItems.length < 50) {
          // If we got fewer than 50 products, we're probably on the last page
          console.log(`FBT: Less than 50 products on page ${page}, stopping`);
          hasMore = false;
        } else {
          page++;
          // Add small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
      } catch (error) {
        console.error(`FBT: Error fetching page ${page}`, error);
        console.error('FBT: Error details:', error.message, error.stack);
        hasMore = false;
      }
    }
    
    console.log('FBT: fetchCollectionProducts complete. Total products:', allProducts.length);
    return allProducts;
  }
  
  processProductsForFBT(products, options) {
    const { demo, currentVendor, currentColorKey, currentTypeList, currentType, maxProducts, includeOriginalProduct, currentProductId, currentProductHandle } = options;
    
    // Find current product if we need to include it
    let currentProduct = null;
    if (includeOriginalProduct && currentProductId) {
      currentProduct = products.find(p => p.id.toString() === currentProductId.toString());
    }
    
    // Categorize products into priority buckets (similar to Liquid logic)
    const buckets = {
      brandcolorSameVendorSameType: [],
      vendorSameType: [],
      brandcolorSameType: [],
      brandcolorSameVendor: [],
      vendor: [],
      brandcolorDiffVendor: [],
      fallbackSameType: [],
      fallbackSameCollection: [],
      fallbackOther: []
    };
    
    products.forEach(product => {
      // Skip current product from categorization if we're including it separately
      if (includeOriginalProduct && currentProductId && product.id.toString() === currentProductId.toString()) {
        return;
      }
      const matchesVendor = product.vendor && currentVendor && 
        product.vendor.toLowerCase().trim() === currentVendor.toLowerCase().trim();
      
      const matchesColor = this.matchesColor(product, currentColorKey);
      const matchesType = this.matchesType(product, currentTypeList, currentType);
      
      // Categorize based on priority
      if (demo === 'kids') {
        if (matchesColor && matchesVendor && matchesType) {
          buckets.brandcolorSameVendorSameType.push(product);
        } else if (matchesVendor && matchesType) {
          buckets.vendorSameType.push(product);
        } else if (matchesColor && matchesType) {
          buckets.brandcolorSameType.push(product);
        } else if (matchesColor && matchesVendor) {
          buckets.brandcolorSameVendor.push(product);
        } else if (matchesVendor) {
          buckets.vendor.push(product);
        } else if (matchesColor) {
          buckets.brandcolorDiffVendor.push(product);
        } else if (matchesType) {
          buckets.fallbackSameType.push(product);
        } else {
          buckets.fallbackOther.push(product);
        }
      } else {
        // Baby or other demos
        if (matchesColor && matchesVendor) {
          buckets.brandcolorSameVendor.push(product);
        } else if (matchesVendor) {
          buckets.vendor.push(product);
        } else if (matchesColor) {
          buckets.brandcolorDiffVendor.push(product);
        } else if (matchesType) {
          buckets.fallbackSameType.push(product);
        } else {
          buckets.fallbackOther.push(product);
        }
      }
    });
    
    // Apply seed-based rotation (similar to Liquid)
    const seed = this.getSeed();
    Object.keys(buckets).forEach(key => {
      if (buckets[key].length > 0) {
        buckets[key] = this.rotateArray(buckets[key], seed);
      }
    });
    
    // Select products in priority order
    const selected = [];
    const priorityOrder = demo === 'kids' ? [
      'brandcolorSameVendorSameType',
      'vendorSameType',
      'brandcolorSameType',
      'brandcolorSameVendor',
      'vendor',
      'brandcolorDiffVendor',
      'fallbackSameType',
      'fallbackSameCollection',
      'fallbackOther'
    ] : [
      'brandcolorSameVendor',
      'vendor',
      'brandcolorDiffVendor',
      'fallbackSameType',
      'fallbackSameCollection',
      'fallbackOther'
    ];
    
    for (const bucketKey of priorityOrder) {
      if (selected.length >= maxProducts) break;
      for (const product of buckets[bucketKey]) {
        if (selected.length >= maxProducts) break;
        // Skip current product if we're including it separately
        if (includeOriginalProduct && currentProductId && product.id.toString() === currentProductId.toString()) {
          continue;
        }
        if (!selected.find(p => p.id === product.id)) {
          selected.push(product);
        }
      }
    }
    
    // Ensure we fill to maxProducts if we have enough products available
    // This handles cases where we might not have filled all slots
    if (selected.length < maxProducts) {
      // Try to fill from all buckets in priority order again
      for (const bucketKey of priorityOrder) {
        if (selected.length >= maxProducts) break;
        for (const product of buckets[bucketKey]) {
          if (selected.length >= maxProducts) break;
          // Skip current product if we're including it separately
          if (includeOriginalProduct && currentProductId && product.id.toString() === currentProductId.toString()) {
            continue;
          }
          if (!selected.find(p => p.id === product.id)) {
            selected.push(product);
          }
        }
      }
    }
    
    // Prepend current product if we're including it
    if (includeOriginalProduct && currentProduct) {
      // Remove current product from selected if it was already added
      const filteredSelected = selected.filter(p => p.id.toString() !== currentProductId.toString());
      // maxProducts from data attribute is already the number of recommendations needed
      // (Liquid already reduced max_products by 1 when show_original_product is true)
      // So we just limit to maxProducts recommendations and prepend current product
      const limitedSelected = filteredSelected.slice(0, maxProducts);
      const result = [currentProduct, ...limitedSelected];
      console.log(`FBT: processProductsForFBT - includeOriginalProduct=true, found ${limitedSelected.length} recommendations (need ${maxProducts}), total with current: ${result.length}`);
      return result;
    }
    
    // Limit to maxProducts (when not including original product, maxProducts is the total count)
    const result = selected.slice(0, maxProducts);
    console.log(`FBT: processProductsForFBT - includeOriginalProduct=false, found ${result.length} products (need ${maxProducts})`);
    return result;
  }
  
  matchesColor(product, colorKey) {
    if (!colorKey) return false;
    
    // Check metafield colors
    if (product.metafields?.color) {
      for (const color of product.metafields.color) {
        if (this.fuzzyColorMatch(color, colorKey)) return true;
      }
    }
    
    // Check tags (if available)
    if (product.tags) {
      for (const tag of product.tags) {
        const tagLower = tag.toLowerCase();
        if (tagLower.includes('brandcolor_') || tagLower.includes('color_')) {
          const tagColor = tagLower.replace(/^(brandcolor_|color_)/, '').trim();
          if (this.fuzzyColorMatch(tagColor, colorKey)) return true;
        }
      }
    }
    
    return false;
  }
  
  normalizeColorToken(color) {
    if (!color) return '';
    return color
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[–—−]/g, '-')      // normalize dash types
      .replace(/[_\s]+/g, '-')     // spaces/underscores to dash
      .replace(/[^a-z0-9-]/g, '')  // strip other chars
      .replace(/-+/g, '-');        // collapse dashes
  }
  
  fuzzyColorMatch(color1, color2) {
    const c1 = this.normalizeColorToken(color1);
    const c2 = this.normalizeColorToken(color2);
    if (!c1 || !c2) return false;
    
    // Allow minor formatting differences (e.g., "navy tartan" vs "navy-tartan")
    const c1Flat = c1.replace(/-/g, '');
    const c2Flat = c2.replace(/-/g, '');
    if (c1Flat === c2Flat) return true;
    
    // Exact match
    if (c1 === c2) return true;
    
    // Contains match
    if (c1.includes(c2) || c2.includes(c1)) return true;
    
    // Color root matching (simplified)
    const root1 = c1.split('-')[0];
    const root2 = c2.split('-')[0];
    if (root1 === root2) return true;
    
    return false;
  }
  
  matchesType(product, typeList, type) {
    if (!typeList.length && !type) return false;
    
    // Check metafield type list
    if (product.metafields?.product_type_filter) {
      for (const productType of product.metafields.product_type_filter) {
        if (typeList.includes(productType)) return true;
        if (type && productType === type) return true;
      }
    }
    
    // Check product.type
    if (product.type) {
      if (typeList.includes(product.type)) return true;
      if (type && product.type === type) return true;
    }
    
    return false;
  }
  
  getSeed() {
    const day = new Date().getDay();
    const productId = this.container.dataset.currentProductId || '0';
    return day + (parseInt(productId) % 97);
  }
  
  rotateArray(arr, seed) {
    if (arr.length === 0) return arr;
    const offset = seed % arr.length;
    return [...arr.slice(offset), ...arr.slice(0, offset)];
  }
  
  renderProductsFromData(products) {
    const container = this.container.querySelector('[data-fbt-products]');
    if (!container) {
      console.error('FBT: Products container not found');
      return;
    }

    // Keep a reference for later variant lookups (e.g., main variant size)
    this.renderedProducts = products;
    
    container.innerHTML = '';
    
    // Ensure container has flex display (CSS expects flex for horizontal layout)
    container.style.display = 'flex';
    
    // Filter out any invalid products and log them
    const validProducts = products.filter(product => {
      if (!product || !product.id) {
        console.warn('FBT: Skipping invalid product (missing id):', product);
        return false;
      }
      if (!product.handle) {
        console.warn('FBT: Skipping product with missing handle:', product.id, product.title);
        return false;
      }
      return true;
    });
    
    if (validProducts.length < products.length) {
      console.warn(`FBT: Filtered out ${products.length - validProducts.length} invalid products. Valid: ${validProducts.length}, Total: ${products.length}`);
    }
    
    // Use GraphQL data directly - no need to fetch product pages!
    validProducts.forEach(product => {
      const productHtml = this.generateProductHTMLFromData(product);
      container.insertAdjacentHTML('beforeend', productHtml);
    });
    
    console.log(`FBT: Rendered ${validProducts.length} products into container (requested: ${products.length})`, container);
  }
  
  async renderProducts(products) {
    // Legacy method - only used if we don't have full product data
    const container = this.container.querySelector('[data-fbt-products]');
    if (!container) return;
    
    container.innerHTML = '';
    
    // Fetch full product data for rendering
    for (const product of products) {
      try {
        const productData = await this.fetchProductData(product.handle);
        if (productData) {
          const productHtml = this.generateProductHTML(productData);
          container.insertAdjacentHTML('beforeend', productHtml);
        }
      } catch (error) {
        console.error(`FBT: Error fetching product ${product.handle}`, error);
      }
    }
  }
  
  async fetchProductData(handle) {
    try {
      console.log(`FBT: fetchProductData called for handle: ${handle}`);
      // Try to fetch product page and extract JSON data
      const response = await fetch(`/products/${handle}`);
      if (!response.ok) {
        console.warn(`FBT: Product page not OK for ${handle}:`, response.status);
        return null;
      }
      
      const html = await response.text();
      console.log(`FBT: Product page HTML length for ${handle}:`, html.length);
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Look for product JSON in script tags (various formats)
      const productJsonSelectors = [
        'script[type="application/json"][data-product-json]',
        'script[type="application/json"][data-product]',
        'script[data-product-json]',
        'script#ProductJson-product-template',
        'script#product-json'
      ];
      
      for (const selector of productJsonSelectors) {
        const productJson = doc.querySelector(selector);
        if (productJson) {
          try {
            const data = JSON.parse(productJson.textContent);
            if (data && data.id) {
              console.log(`FBT: Found product JSON for ${handle} using selector: ${selector}`);
              return data;
            }
          } catch (e) {
            console.warn(`FBT: Error parsing product JSON from ${selector}`, e);
          }
        }
      }
      
      // Fallback: try to extract from window.Shopify object or other patterns
      const scripts = doc.querySelectorAll('script:not([src])');
      for (const script of scripts) {
        const scriptText = script.textContent;
        
        // Try Shopify.theme.product
        if (scriptText.includes('Shopify.theme.product')) {
          try {
            const match = scriptText.match(/Shopify\.theme\.product\s*=\s*({[\s\S]*?});/);
            if (match) {
              const data = JSON.parse(match[1]);
              if (data && data.id) {
                console.log(`FBT: Found product in Shopify.theme.product for ${handle}`);
                return data;
              }
            }
          } catch (e) {
            // Continue
          }
        }
        
        // Try window.productData or similar
        if (scriptText.includes('product') && scriptText.includes('{')) {
          try {
            // Look for common patterns like window.productData = {...}
            const patterns = [
              /window\.productData\s*=\s*({[\s\S]*?});/,
              /var\s+product\s*=\s*({[\s\S]*?});/,
              /const\s+product\s*=\s*({[\s\S]*?});/,
              /let\s+product\s*=\s*({[\s\S]*?});/,
              /product\s*:\s*({[\s\S]*?}),/,
              /"product"\s*:\s*({[\s\S]*?}),/
            ];
            
            for (const pattern of patterns) {
              const match = scriptText.match(pattern);
              if (match) {
                try {
                  const data = JSON.parse(match[1]);
                  if (data && (data.id || data.handle === handle)) {
                    console.log(`FBT: Found product data using pattern for ${handle}`);
                    return data;
                  }
                } catch (e) {
                  // Continue to next pattern
                }
              }
            }
          } catch (e) {
            // Continue
          }
        }
      }
      
      // Last resort: try to extract basic info from HTML
      console.warn(`FBT: No product JSON found for ${handle}, attempting HTML extraction...`);
      const titleEl = doc.querySelector('h1.product-title, h1[data-product-title], .product-title h1');
      const priceEl = doc.querySelector('[data-product-price], .product-price, .price');
      const vendorEl = doc.querySelector('[data-product-vendor], .product-vendor');
      const typeEl = doc.querySelector('[data-product-type], .product-type');
      
      if (titleEl) {
        // At least we have a title, create minimal product object
        const minimalProduct = {
          handle: handle,
          title: titleEl.textContent.trim(),
          vendor: vendorEl ? vendorEl.textContent.trim() : '',
          type: typeEl ? typeEl.textContent.trim() : '',
          id: null, // We'll need to get this from somewhere else
          variants: [],
          images: [],
          metafields: { custom: {} }
        };
        
        console.warn(`FBT: Created minimal product object for ${handle} (no ID - will skip)`);
        // Return null since we don't have an ID
        return null;
      }
      
      console.warn(`FBT: No product data found for ${handle}`);
      return null;
    } catch (error) {
      console.error(`FBT: Error fetching product data for ${handle}`, error);
      return null;
    }
  }
  
  generateShopifyImageSrcset(imageUrl, widths = [200, 400, 600]) {
    if (!imageUrl) return '';
    
    // Ensure URL is properly formatted
    let url = imageUrl;
    if (url.startsWith('//')) {
      url = `https:${url}`;
    } else if (!url.startsWith('http')) {
      url = `https://${url}`;
    }
    
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      
      // Generate srcset entries for each width
      const srcsetEntries = widths.map(width => {
        // Shopify image URLs: add size before extension (e.g., image_200x200.jpg)
        // Check if URL already has a size parameter
        const sizeMatch = pathname.match(/_(\d+)x(\d+)(\.[^.]+)$/);
        let newPathname;
        
        if (sizeMatch) {
          // Replace existing size
          newPathname = pathname.replace(/_(\d+)x(\d+)(\.[^.]+)$/, `_${width}x${width}$3`);
        } else {
          // Add size before extension
          const extMatch = pathname.match(/(\.[^.]+)$/);
          if (extMatch) {
            const ext = extMatch[1];
            const beforeExt = pathname.substring(0, pathname.lastIndexOf(ext));
            newPathname = `${beforeExt}_${width}x${width}${ext}`;
          } else {
            // No extension found, append size
            newPathname = `${pathname}_${width}x${width}`;
          }
        }
        
        urlObj.pathname = newPathname;
        return `${urlObj.toString()} ${width}w`;
      });
      
      return srcsetEntries.join(', ');
    } catch (e) {
      // If URL parsing fails, return empty srcset (fallback to single image)
      console.warn('FBT: Failed to generate srcset for image URL:', url, e);
      return '';
    }
  }
  
  generateProductHTMLFromData(product) {
    // Generate HTML from GraphQL data (faster - no need to fetch product pages)
    const productId = product.id;
    const firstVariant = product.variants?.[0];
    const price = this.toCents(firstVariant?.price ?? product.price ?? 0);
    const comparePrice = this.toCents(firstVariant?.compare_at_price ?? 0);
    let image = product.image || product.featured_image || product.images?.[0] || '';
    
    // Ensure image URL is properly formatted
    if (image) {
      if (image.startsWith('//')) {
        image = `https:${image}`;
      } else if (!image.startsWith('http')) {
        image = `https://${image}`;
      }
    }
    
    const title = product.title || '';
    const handle = product.handle || '';
    
    // Escape HTML to prevent XSS
    const escapeHtml = (text) => {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };
    
    // Generate responsive image srcset
    const imageSrcset = image ? this.generateShopifyImageSrcset(image, [200, 400, 600]) : '';
    // Use 400px (2x) as default src for better fallback
    let defaultImageUrl = image;
    if (image && imageSrcset) {
      try {
        // Extract 400w URL from srcset
        const srcset400 = this.generateShopifyImageSrcset(image, [400]);
        if (srcset400) {
          defaultImageUrl = srcset400.split(' ')[0];
        }
      } catch (e) {
        // Fallback to original image
        defaultImageUrl = image;
      }
    }
    
    // Parse title to extract color/subtitle (format: "Product Name - Color Name")
    const titleParts = title.split(' - ');
    const mainTitle = titleParts[0];
    const subtitle = titleParts.length > 1 ? titleParts[titleParts.length - 1] : (product.type || '');
    
    // Get vendor
    const vendor = product.vendor || '';
    
    // Get first available variant or first variant
    const preferredVariant = this.getPreferredVariant(product.variants);
    const selectedVariant = preferredVariant || product.variants?.find(v => v.available !== false) || firstVariant || product.variants?.[0];
    const selectedVariantId = selectedVariant?.id || firstVariant?.id || '';
    const selectedVariantPrice = this.toCents(selectedVariant?.price ?? price);
    const selectedVariantComparePrice = this.toCents(selectedVariant?.compare_at_price ?? comparePrice);
    
    return `
      <div class="fbt-product-item" data-product-id="${productId}">
        <div class="fbt-product-checkbox">
          <input 
            type="checkbox" 
            id="fbt-${this.container.dataset.sectionId}-${productId}" 
            class="fbt-checkbox"
            data-product-id="${productId}"
            data-product-price="${selectedVariantPrice}"
            data-product-compare-price="${selectedVariantComparePrice}"
            checked
          >
          <label for="fbt-${this.container.dataset.sectionId}-${productId}" class="fbt-checkbox-label">
            <span class="fbt-checkbox-custom"></span>
          </label>
        </div>

        <div class="fbt-product-content">
          <div class="fbt-product-image">
            <a href="/products/${handle}" class="fbt-product-link">
              ${image ? `
                <img 
                  src="${escapeHtml(defaultImageUrl || image)}" 
                  ${imageSrcset ? `srcset="${escapeHtml(imageSrcset)}"` : ''}
                  alt="${escapeHtml(title)}" 
                  class="fbt-product-img"
                  width="200"
                  height="200"
                  loading="lazy"
                  sizes="(max-width: 749px) 150px, 200px"
                >
              ` : `
                <div class="fbt-product-placeholder">
                  <svg class="fbt-placeholder-icon" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
              `}
            </a>
          </div>

          <div class="fbt-product-details">
            <h3 class="fbt-product-title">
              <a href="/products/${handle}">${escapeHtml(mainTitle)}</a>
            </h3>
            ${subtitle ? `<div class="fbt-product-subtitle">${escapeHtml(subtitle)}</div>` : ''}
            ${vendor ? `<div class="fbt-product-brand">${escapeHtml(vendor)}</div>` : ''}

            <div class="fbt-product-variants">
              ${product.variants && product.variants.length > 1 ? `
                <select class="fbt-variant-select" data-product-id="${productId}">
                  ${product.variants.map(v => {
                    const isSelected = v.id === selectedVariantId;
                    return `
                      <option 
                        value="${v.id}" 
                        data-price="${v.price}" 
                        data-compare-price="${v.compare_at_price || 0}" 
                        ${isSelected ? 'selected' : ''}
                        ${v.available === false ? 'disabled' : ''}
                      >
                        ${escapeHtml(v.title || 'Default')}${v.available === false ? ' - Sold Out' : ''}
                      </option>
                    `;
                  }).join('')}
                </select>
              ` : `
                <input 
                  type="hidden" 
                  class="fbt-variant-select" 
                  data-product-id="${productId}"
                  value="${selectedVariantId}"
                  data-price="${selectedVariantPrice}"
                  data-compare-price="${selectedVariantComparePrice}"
                >
              `}
            </div>

            <div class="fbt-product-price">
              <span class="fbt-price-current" data-price="${selectedVariantPrice}">${this.formatMoney(selectedVariantPrice)}</span>
              ${selectedVariantComparePrice > selectedVariantPrice ? `
                <span class="fbt-price-compare" data-compare-price="${selectedVariantComparePrice}">${this.formatMoney(selectedVariantComparePrice)}</span>
                ${this.renderSavingsDisplay(selectedVariantPrice, selectedVariantComparePrice)}
              ` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }
  
  generateProductHTML(product) {
    // Legacy method - for when we fetch product pages
    const productId = product.id || product.variants?.[0]?.product_id;
    const preferredVariant = this.getPreferredVariant(product.variants);
    const variant = preferredVariant || product.variants?.[0];
    const variantId = variant?.id;
    const price = this.toCents(variant?.price || 0);
    const comparePrice = this.toCents(variant?.compare_at_price || 0);
    let image = product.featured_image || product.images?.[0] || '';
    
    // Ensure image URL is properly formatted
    if (image) {
      if (image.startsWith('//')) {
        image = `https:${image}`;
      } else if (!image.startsWith('http')) {
        image = `https://${image}`;
      }
    }
    
    const title = product.title || '';
    const handle = product.handle || '';
    
    // Escape HTML to prevent XSS
    const escapeHtml = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };
    
    // Generate responsive image srcset
    const imageSrcset = image ? this.generateShopifyImageSrcset(image, [200, 400, 600]) : '';
    // Use 400px (2x) as default src for better fallback
    let defaultImageUrl = image;
    if (image && imageSrcset) {
      try {
        // Extract 400w URL from srcset
        const srcset400 = this.generateShopifyImageSrcset(image, [400]);
        if (srcset400) {
          defaultImageUrl = srcset400.split(' ')[0];
        }
      } catch (e) {
        // Fallback to original image
        defaultImageUrl = image;
      }
    }
    
    return `
      <div class="fbt-product-item" data-product-id="${productId}">
        <div class="fbt-product-checkbox">
          <input 
            type="checkbox" 
            id="fbt-${this.container.dataset.sectionId}-${productId}" 
            class="fbt-checkbox"
            data-product-id="${productId}"
            data-product-price="${price}"
            data-product-compare-price="${comparePrice}"
            checked
          >
          <label for="fbt-${this.container.dataset.sectionId}-${productId}" class="fbt-checkbox-label"></label>
        </div>
        <a href="/products/${handle}" class="fbt-product-link">
          ${image ? `<img src="${escapeHtml(defaultImageUrl || image)}" ${imageSrcset ? `srcset="${escapeHtml(imageSrcset)}"` : ''} alt="${escapeHtml(title)}" class="fbt-product-image" width="200" height="200" loading="lazy" sizes="(max-width: 749px) 150px, 200px">` : '<div class="fbt-product-image-placeholder">No Image</div>'}
        </a>
        <div class="fbt-product-info">
          <a href="/products/${handle}" class="fbt-product-title">${escapeHtml(title)}</a>
          <div class="fbt-product-price">
            <span class="fbt-price-current" data-price="${price}">${this.formatMoney(price)}</span>
            ${comparePrice > price ? `
              <span class="fbt-price-compare" data-compare-price="${comparePrice}">${this.formatMoney(comparePrice)}</span>
              ${this.renderSavingsDisplay(price, comparePrice)}
            ` : ''}
          </div>
          <select class="fbt-variant-select" data-product-id="${productId}">
            ${product.variants?.map(v => `
              <option value="${v.id}" data-price="${v.price}" data-compare-price="${v.compare_at_price || 0}" ${v.available ? '' : 'disabled'}>
                ${escapeHtml(v.title || 'Default')}${v.available ? '' : ' - Sold Out'}
              </option>
            `).join('') || ''}
          </select>
        </div>
      </div>
    `;
  }
  
  selectAllProductsByDefault() {
    // Select all products by default (except those that are disabled)
    this.checkboxes.forEach(checkbox => {
      if (!checkbox.disabled) {
        checkbox.checked = true;
        // Add visual feedback
        const productItem = checkbox.closest('.fbt-product-item');
        if (productItem) {
          productItem.classList.add('fbt-selected');
        }
      }
    });
  }

  bindEvents() {
    // Checkbox change events
    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        this.handleCheckboxChange(e);
        this.updateTotal();
        this.trackEvent('product_selected', {
          product_id: e.target.dataset.productId,
          selected: e.target.checked
        });
      });
    });
    
    // Variant select change events
    this.variantSelects.forEach(select => {
      select.addEventListener('change', (e) => {
        const productId = e.target.dataset.productId;
        this.updateProductPrice(productId, e.target);
        this.updateTotal();
        this.trackEvent('variant_changed', {
          product_id: productId,
          variant_id: e.target.value
        });
      });
    });
    
    // Add to cart button
    this.addToCartBtn?.addEventListener('click', () => this.addSelectedToCart());
    
    // Product image click tracking
    this.container.querySelectorAll('.fbt-product-link').forEach(link => {
      link.addEventListener('click', (e) => {
        const productId = e.target.closest('.fbt-product-item').dataset.productId;
        this.trackEvent('product_viewed', { product_id: productId });
      });
    });
  }
  
  listenToMainProductVariantChanges() {
    // Find the main product section
    const mainProductSection = document.querySelector('[data-section-id][data-product-id]');
    if (!mainProductSection) return;
    
    const mainSectionId = mainProductSection.dataset.sectionId;
    const mainProductId = mainProductSection.dataset.productId;
    
    // Listen for variant change events from the main product
    const eventName = `variant:change:${mainSectionId}:${mainProductId}`;
    
    document.addEventListener(eventName, (event) => {
      const { variant } = event.detail;
      if (!variant || !variant.title) return;
      
      // Extract the variant title (size) from the main product
      const mainVariantTitle = variant.title?.trim() || this.resolveVariantTitleById(variant.id);
      this.mainVariantId = variant.id?.toString() || this.mainVariantId;
      this.mainVariantTitle = mainVariantTitle;
      this.mainVariantSize = this.getMainVariantSizeToken(mainVariantTitle);
      
      // Update FBT variant selectors to match if available
      this.updateFbtVariantsToMatch(mainVariantTitle, this.mainVariantSize, this.mainVariantId);
    });

    // Fallback: listen to form inputs in case the theme doesn't dispatch the custom event
    const form = mainProductSection.querySelector('form[action*="/cart/add"]');
    if (form) {
      const variantInputs = form.querySelectorAll('select[name="id"], input[name="id"]');
      variantInputs.forEach(input => {
        input.addEventListener('change', () => {
          // Try to grab the displayed label/title for the selected option/radio
          let title = '';
          if (input.tagName === 'SELECT') {
            const opt = input.options[input.selectedIndex];
            if (opt?.textContent) title = opt.textContent.trim();
          } else if (input.type === 'radio') {
            const label = input.closest('label');
            if (label?.textContent) title = label.textContent.trim();
          }
          if (!title) {
            // fallback: resolve by variant id
            const vid = input.value;
            title = this.resolveVariantTitleById(vid);
          }

          if (title) {
            this.mainVariantTitle = title;
            this.mainVariantId = input.value || this.mainVariantId;
            this.mainVariantSize = this.getMainVariantSizeToken(title);
            this.updateFbtVariantsToMatch(title, this.mainVariantSize, this.mainVariantId);
          }
        });
      });
    }
  }
  
  updateFbtVariantsToMatch(mainVariantTitle, mainVariantSizeToken = null, mainVariantId = null) {
    // Normalize the variant title for comparison (case-insensitive, trim whitespace)
    // Remove extra spaces and normalize formatting
    const normalizedMainTitle = this.normalizeVariantTitle(mainVariantTitle);
    const normalizedMainSize = mainVariantSizeToken || this.getMainVariantSizeToken(mainVariantTitle);
    const mainNumericSize = this.extractNumericSize(normalizedMainSize);
    const altSizes = this.buildAltSizeTokens(normalizedMainSize, mainNumericSize);
    // Always refresh selects after dynamic rendering
    this.variantSelects = this.container.querySelectorAll('.fbt-variant-select');
    
    // Iterate through all FBT variant selects
    this.variantSelects.forEach(select => {
      if (select.tagName !== 'SELECT') return;
      
      const match = this.findOptionForVariant(select, {
        mainVariantId,
        normalizedMainSize,
        mainNumericSize,
        normalizedMainTitle,
        altSizes
      });

      if (match.option) {
        const previousValue = select.value;
        select.value = match.option.value;
        select.selectedIndex = match.index;
        Array.from(select.options).forEach((opt, idx) => {
          opt.selected = idx === match.index;
        });

        if (previousValue !== match.option.value) {
          select.dispatchEvent(new Event('change', { bubbles: true }));
          select.dispatchEvent(new Event('input', { bubbles: true }));

          this.trackEvent('fbt_variant_synced', {
            product_id: select.dataset.productId,
            variant_id: match.option.value,
            main_variant_title: mainVariantTitle
          });
        }
      }
    });
  }

  findOptionBySize(select, normalizedSize, numericSize, altSizes = []) {
    let best = { option: null, index: -1 };
    const opts = Array.from(select.options || []);
    // Exact size token
    if (normalizedSize) {
      const idx = opts.findIndex(o => this.normalizeSizeToken(o.textContent.trim()) === normalizedSize && !o.disabled);
      if (idx >= 0) return { option: opts[idx], index: idx };
    }
    // Alternate tokens
    if (altSizes.length) {
      const idx = opts.findIndex(o => {
        const tok = this.normalizeSizeToken(o.textContent.trim());
        return altSizes.includes(tok) && !o.disabled;
      });
      if (idx >= 0) return { option: opts[idx], index: idx };
    }
    // Numeric size
    if (numericSize) {
      const idx = opts.findIndex(o => this.extractNumericSize(this.normalizeSizeToken(o.textContent.trim())) === numericSize && !o.disabled);
      if (idx >= 0) return { option: opts[idx], index: idx };
    }
    return best;
  }

  findOptionForVariant(select, { mainVariantId, normalizedMainSize, mainNumericSize, normalizedMainTitle, altSizes }) {
    const opts = Array.from(select.options || []);

    // 1) Match by variant id
    if (mainVariantId) {
      const idx = opts.findIndex(o => o.value?.toString() === mainVariantId && !o.disabled);
      if (idx >= 0) return { option: opts[idx], index: idx };
    }

    // 2) Exact size token
    if (normalizedMainSize) {
      const idx = opts.findIndex(o => this.normalizeSizeToken(o.textContent.trim()) === normalizedMainSize && !o.disabled);
      if (idx >= 0) return { option: opts[idx], index: idx };
    }

    // 2b) Alternate size tokens (e.g., 4t vs 4, 4yr vs 4y)
    if (altSizes?.length) {
      const idx = opts.findIndex(o => {
        const tok = this.normalizeSizeToken(o.textContent.trim());
        return altSizes.includes(tok) && !o.disabled;
      });
      if (idx >= 0) return { option: opts[idx], index: idx };
    }

    // 3) Numeric size heuristic
    if (mainNumericSize) {
      const idx = opts.findIndex(o => this.extractNumericSize(this.normalizeSizeToken(o.textContent.trim())) === mainNumericSize && !o.disabled);
      if (idx >= 0) return { option: opts[idx], index: idx };
    }

    // 4) Title fallback
    if (normalizedMainTitle) {
      const idx = opts.findIndex(o => this.normalizeVariantTitle(o.textContent.trim()) === normalizedMainTitle && !o.disabled);
      if (idx >= 0) return { option: opts[idx], index: idx };
    }

    return { option: null, index: -1 };
  }

  buildAltSizeTokens(normalizedSize, numeric) {
    const out = [];
    if (!normalizedSize) return out;
    if (numeric) {
      // common variants: 4t, 4, 4y, 4yr, 4years, 4 yrs, 4 years
      const bases = [`${numeric}`, `${numeric}t`, `${numeric}y`, `${numeric}yr`, `${numeric}yrs`, `${numeric}year`, `${numeric}years`];
      bases.forEach(b => out.push(this.normalizeSizeToken(b)));
    }
    return Array.from(new Set(out));
  }
  
  handleCheckboxChange(event) {
    const checkbox = event.target;
    const productItem = checkbox.closest('.fbt-product-item');
    
    // Add visual feedback
    if (checkbox.checked) {
      productItem.classList.add('fbt-selected');
    } else {
      productItem.classList.remove('fbt-selected');
    }
  }
  
  updateProductPrice(productId, selectElement) {
    const productItem = this.container.querySelector(`[data-product-id="${productId}"]`);
    if (!productItem) return;
    
    const priceElement = productItem.querySelector('.fbt-price-current');
    const comparePriceElement = productItem.querySelector('.fbt-price-compare');
    const savingsElement = productItem.querySelector('[data-savings]');
    if (!priceElement) return;
    
    let newPrice, newComparePrice;
    
    // Handle both select elements and hidden inputs
    if (selectElement.tagName === 'SELECT') {
      const selectedOption = selectElement.options[selectElement.selectedIndex];
      newPrice = this.toCents(selectedOption.dataset.price);
      newComparePrice = this.toCents(selectedOption.dataset.comparePrice || 0);
    } else if (selectElement.tagName === 'INPUT') {
      // Hidden input for single-variant products
      newPrice = this.toCents(selectElement.dataset.price);
      newComparePrice = this.toCents(selectElement.dataset.comparePrice || 0);
    } else {
      return;
    }
    
    priceElement.textContent = this.formatMoney(newPrice);
    priceElement.dataset.price = newPrice;
    
    if (comparePriceElement) {
      if (newComparePrice > newPrice) {
        comparePriceElement.textContent = this.formatMoney(newComparePrice);
        comparePriceElement.dataset.comparePrice = newComparePrice;
        comparePriceElement.style.display = 'inline';
        
        // Update savings display if element exists (means product_save_amount setting is enabled)
        if (savingsElement) {
          const saveType = (window.theme?.settings?.saveType || 'percent').toLowerCase();
          let savedAmount;
          
          if (saveType === 'dollar') {
            const savingsCents = newComparePrice - newPrice;
            savedAmount = this.formatMoney(savingsCents).replace(/[^\d.,]/g, ''); // Remove currency symbols
          } else {
            // Calculate percentage
            const savingsPercent = Math.round(((newComparePrice - newPrice) / newComparePrice) * 100);
            savedAmount = `${savingsPercent}%`;
          }
          
          // Update the savings text - try to preserve the translation format
          const currentText = savingsElement.textContent.trim();
          if (currentText.includes('Save') || currentText.includes('off')) {
            savingsElement.textContent = `Save ${savedAmount}`;
          } else {
            savingsElement.textContent = `${savedAmount} off`;
          }
          savingsElement.style.display = 'inline';
        }
      } else {
        comparePriceElement.style.display = 'none';
        if (savingsElement) {
          savingsElement.style.display = 'none';
        }
      }
    } else if (savingsElement) {
      // Hide savings if no compare price
      savingsElement.style.display = 'none';
    }
  }
  
  updateTotal() {
    let total = 0;
    let selectedCount = 0;
    const selectedProducts = [];
    
    // Refresh checkboxes and variant selects after dynamic rendering
    this.checkboxes = this.container.querySelectorAll('.fbt-checkbox');
    this.variantSelects = this.container.querySelectorAll('.fbt-variant-select');
    
    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const productId = checkbox.dataset.productId;
        const productItem = this.container.querySelector(`[data-product-id="${productId}"]`);
        
        if (!productItem) {
          console.warn(`FBT: Product item not found for ID: ${productId}`);
          return;
        }
        
        const variantSelect = productItem.querySelector('.fbt-variant-select');
        
        if (!variantSelect) {
          console.warn(`FBT: Variant select not found for product: ${productId}`);
          return;
        }
        
        let price;
        if (variantSelect.tagName === 'SELECT') {
          const selectedOption = variantSelect.options[variantSelect.selectedIndex];
          if (!selectedOption || !selectedOption.dataset.price) {
            console.warn(`FBT: No price data for variant in product: ${productId}`);
            return;
          }
          price = this.toCents(selectedOption.dataset.price);
        } else {
          if (!variantSelect.dataset.price) {
            console.warn(`FBT: No price data for product: ${productId}`);
            return;
          }
          price = this.toCents(variantSelect.dataset.price);
        }
        
        total += price;
        selectedCount++;
        selectedProducts.push({
          id: productId,
          price: price
        });
      }
    });
    
    this.totalPriceElement.textContent = this.formatMoney(total);
    
    // Update add to cart button state
    this.addToCartBtn.disabled = selectedCount === 0;
    
    // Calculate and show discount if enabled
    if (this.enableDiscount && selectedCount > 1) {
      let discount = 0;
      if (this.discountPercentage > 0) {
        discount = total * (this.discountPercentage / 100);
      } else if (this.discountValue > 0) {
        discount = this.discountValue;
      }
      
      if (discount > 0) {
        const finalTotal = total - discount;
        if (this.discountSection) this.discountSection.style.display = 'block';
        if (this.discountAmountEl) this.discountAmountEl.textContent = `-${this.formatMoney(discount)}`;
        if (this.finalTotalSection) this.finalTotalSection.style.display = 'block';
        if (this.finalTotalPrice) this.finalTotalPrice.textContent = this.formatMoney(finalTotal);
        
        // Track discount view
        this.trackEvent('discount_applied', {
          discount_amount: discount,
          final_total: finalTotal,
          selected_count: selectedCount
        });
      } else {
        if (this.discountSection) this.discountSection.style.display = 'none';
        if (this.finalTotalSection) this.finalTotalSection.style.display = 'none';
      }
    } else {
      if (this.discountSection) this.discountSection.style.display = 'none';
      if (this.finalTotalSection) this.finalTotalSection.style.display = 'none';
    }
    
    // Track total update
    this.trackEvent('total_updated', {
      total: total,
      selected_count: selectedCount,
      selected_products: selectedProducts
    });
  }
  
  async addSelectedToCart() {
    const selectedItems = [];
    const selectedProducts = [];
    
    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const productId = checkbox.dataset.productId;
        const productItem = this.container.querySelector(`[data-product-id="${productId}"]`);
        const variantSelect = productItem.querySelector('.fbt-variant-select');
        
        let variantId;
        if (variantSelect.tagName === 'SELECT') {
          variantId = variantSelect.value;
        } else {
          variantId = variantSelect.value;
        }
        
        selectedItems.push({
          id: variantId,
          quantity: 1
        });
        
        selectedProducts.push({
          product_id: productId,
          variant_id: variantId
        });
      }
    });
    
    if (selectedItems.length === 0) return;
    
    this.setLoadingState(true);
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: selectedItems,
          // Include sections for cart refresh - same as quick-add.js
          sections: ['cart-ajax']
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Dispatch the same event that quick-add.js uses for cart refresh
        // This is the event that cart-drawer.js listens for
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          bubbles: true,
          detail: {
            product: result,
            addToCartBtn: this.addToCartBtn,
            items: selectedItems,
            preventCartOpen: false // Allow cart drawer to open
          }
        }));
        
        // Also dispatch the cart:build event for additional cart refresh
        document.dispatchEvent(new CustomEvent('cart:build'));
        
        // Show success message
        this.showSuccessMessage(selectedItems.length);
        
        // Track successful add to cart
        this.trackEvent('fbt_add_to_cart_success', {
          items_count: selectedItems.length,
          products: selectedProducts,
          total_value: this.calculateTotalValue()
        });
        
        // Optional: Redirect to cart or show cart drawer
        this.handlePostAddToCart();
        
      } else {
        const error = await response.json();
        this.showErrorMessage(error.description || 'Failed to add items to cart');
        
        // Track failed add to cart
        this.trackEvent('fbt_add_to_cart_error', {
          error: error.description,
          products: selectedProducts
        });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorMessage('Failed to add items to cart');
      
      // Track error
      this.trackEvent('fbt_add_to_cart_error', {
        error: error.message,
        products: selectedProducts
      });
    } finally {
      this.setLoadingState(false);
    }
  }
  
  setLoadingState(loading) {
    if (this.addToCartBtn) this.addToCartBtn.disabled = loading;
    if (loading) {
      if (this.btnText) this.btnText.style.display = 'none';
      if (this.btnLoading) this.btnLoading.style.display = 'inline-flex';
    } else {
      if (this.btnText) this.btnText.style.display = 'inline';
      if (this.btnLoading) this.btnLoading.style.display = 'none';
    }
  }
  
  showSuccessMessage(itemCount) {
    // Remove existing success messages
    document.querySelectorAll('.fbt-success-message').forEach(msg => msg.remove());
    
    const message = document.createElement('div');
    message.className = 'fbt-success-message';
    message.textContent = `${itemCount} item${itemCount > 1 ? 's' : ''} added to cart successfully!`;
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      color: #181818;
      background: #ffffff;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 1000;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(message);
    
    setTimeout(() => {
      message.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => message.remove(), 300);
    }, 3000);
  }
  
  showErrorMessage(text) {
    // Remove existing error messages
    document.querySelectorAll('.fbt-error-message').forEach(msg => msg.remove());
    
    const message = document.createElement('div');
    message.className = 'fbt-error-message';
    message.textContent = text;
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f44336;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 1000;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(message);
    
    setTimeout(() => {
      message.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => message.remove(), 300);
    }, 5000);
  }
  
  
  handlePostAddToCart() {
    // Check if we should redirect to cart or show cart drawer
    const shouldRedirect = this.container.dataset.redirectToCart === 'true';
    const shouldShowDrawer = this.container.dataset.showCartDrawer === 'true';
    
    if (shouldRedirect) {
      window.location.href = '/cart';
    } else if (shouldShowDrawer) {
      // Trigger cart drawer if it exists
      const cartDrawer = document.querySelector('cart-drawer, .cart-drawer');
      if (cartDrawer && cartDrawer.open) {
        cartDrawer.open();
      }
    }
  }
  
  calculateTotalValue() {
    let total = 0;
    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const productId = checkbox.dataset.productId;
        const productItem = this.container.querySelector(`[data-product-id="${productId}"]`);
        const variantSelect = productItem.querySelector('.fbt-variant-select');
        
        let price;
        if (variantSelect.tagName === 'SELECT') {
          const selectedOption = variantSelect.options[variantSelect.selectedIndex];
          price = this.toCents(selectedOption.dataset.price);
        } else {
          price = this.toCents(variantSelect.dataset.price);
        }
        
        total += price;
      }
    });
    return total;
  }
  
  initializeAnalytics() {
    // Track FBT section view
    this.trackEvent('fbt_section_viewed', {
      section_id: this.container.dataset.sectionId,
      product_count: this.products.length
    });
  }
  
  trackEvent(eventName, data = {}) {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        event_category: 'Frequently Bought Together',
        ...data
      });
    }
    
    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'CustomEvent', {
        event_name: eventName,
        ...data
      });
    }
    
    // Shopify Analytics
    if (window.Shopify && window.Shopify.analytics) {
      window.Shopify.analytics.publish(eventName, data);
    }
    
    // Custom event for other tracking systems
    document.dispatchEvent(new CustomEvent('fbt:track', {
      detail: {
        event: eventName,
        data: data
      }
    }));
  }
  
  toCents(value) {
    if (value === null || value === undefined) return 0;
    const sanitized = typeof value === 'string' ? value.replace(/,/g, '') : value;
    const num = Number(sanitized);
    if (!Number.isFinite(num)) return 0;
    // Treat decimal values as dollars, integer-like values as cents
    if (Math.abs(num) < 100 && value.toString().includes('.')) {
      return Math.round(num * 100);
    }
    return Math.round(num);
  }
  
  formatMoney(cents) {
    const normalized = this.toCents(cents);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency
    }).format(normalized / 100);
  }

  renderSavingsDisplay(price, comparePrice) {
    if (!comparePrice || comparePrice <= price) return '';
    
    const saveType = (window.theme?.settings?.saveType || 'percent').toLowerCase();
    let savedAmount;
    
    if (saveType === 'dollar') {
      const savingsCents = comparePrice - price;
      savedAmount = this.formatMoney(savingsCents).replace(/[^\d.,]/g, ''); // Remove currency symbols
    } else {
      // Calculate percentage
      const savingsPercent = Math.round(((comparePrice - price) / comparePrice) * 100);
      savedAmount = `${savingsPercent}%`;
    }
    
    return `<span class="fbt-price-savings grid-product__price--savings" data-savings>Save ${savedAmount}</span>`;
  }

  normalizeVariantTitle(title) {
    if (!title) return '';
    return title
      .toString()
      .replace(/\s*-\s*Sold\s*Out\s*$/i, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');
  }

  normalizeSizeToken(value) {
    if (!value) return '';
    const raw = value.toString().toLowerCase().replace(/[–—−]/g, '-'); // normalize dashes
    return raw
      .replace(/\s*-\s*Sold\s*Out\s*$/gi, '')
      .replace(/months?/gi, 'm')
      .replace(/yrs?|years?/gi, 'y')
      .replace(/t\b/gi, 't') // keep T sizes (2T, 3T, 4T)
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  extractNumericSize(sizeToken) {
    if (!sizeToken) return '';
    // If the token is clearly months (contains m and not y), avoid numeric-year fallbacks
    if (sizeToken.includes('m') && !sizeToken.includes('y')) return '';
    const match = sizeToken.match(/^(\d+)/);
    return match ? match[1] : '';
  }

  getPreferredVariant(variants = []) {
    if ((!this.mainVariantTitle && !this.mainVariantSize) || !variants.length) {
      console.log('FBT: getPreferredVariant - early return (no title/size or no variants)', {
        mainVariantTitle: this.mainVariantTitle,
        mainVariantSize: this.mainVariantSize,
        variantsLength: variants.length
      });
      return null;
    }
    
    const normalizedMainTitle = this.normalizeVariantTitle(this.mainVariantTitle || '');
    const normalizedMainSize = this.mainVariantSize || this.normalizeSizeToken(this.mainVariantTitle);
    const mainNumericSize = this.extractNumericSize(normalizedMainSize);
    
    console.log('FBT: getPreferredVariant - matching with:', {
      mainVariantId: this.mainVariantId,
      normalizedMainTitle,
      normalizedMainSize,
      mainNumericSize,
      firstVariant: variants[0]?.title
    });

    // Match by variant id first if available (only for the main product)
    if (this.mainVariantId) {
      const byId = variants.find(v => v.id?.toString() === this.mainVariantId && v.available !== false);
      if (byId) {
        console.log('FBT: getPreferredVariant - matched by ID (available):', byId.title);
        return byId;
      }
    }

    // Match by exact size token - only return if available
    const bySizeAvailable = normalizedMainSize
      ? variants.find(v => this.normalizeSizeToken(v.title) === normalizedMainSize && v.available !== false)
      : null;
    if (bySizeAvailable) {
      console.log('FBT: getPreferredVariant - matched by size (available):', bySizeAvailable.title);
      return bySizeAvailable;
    }

    // Match by numeric size - only return if available
    if (mainNumericSize) {
      const byNumericAvailable = variants.find(v => this.extractNumericSize(this.normalizeSizeToken(v.title)) === mainNumericSize && v.available !== false);
      if (byNumericAvailable) {
        console.log('FBT: getPreferredVariant - matched by numeric (available):', byNumericAvailable.title);
        return byNumericAvailable;
      }
    }

    // Match by title - only return if available
    const byTitleAvailable = normalizedMainTitle
      ? variants.find(v => this.normalizeVariantTitle(v.title) === normalizedMainTitle && v.available !== false)
      : null;
    if (byTitleAvailable) {
      console.log('FBT: getPreferredVariant - matched by title (available):', byTitleAvailable.title);
      return byTitleAvailable;
    }

    // No available match found - return null to trigger fallback to first available variant
    console.log('FBT: getPreferredVariant - no available match found, returning null (will use first available)');
    return null;
  }

  getMainVariantTitle() {
    try {
      const mainProductSection = document.querySelector('[data-section-id][data-product-id]');
      if (!mainProductSection) return null;

      // 1) Select dropdown (common)
      const select = mainProductSection.querySelector('select[name="id"]');
      if (select && select.options.length) {
        const opt = select.options[select.selectedIndex];
        if (opt?.textContent) return opt.textContent.trim();
      }

      // 2) Radio/hidden inputs
      const input = mainProductSection.querySelector('[name="id"][type="radio"]:checked,[name="id"][type="hidden"]');
      if (input) {
        const label = input.closest('label');
        if (label?.textContent) return label.textContent.trim();
      }

      // 3) Data attribute fallback (theme-provided)
      if (mainProductSection.dataset.selectedVariantTitle) {
        return mainProductSection.dataset.selectedVariantTitle;
      }

      // 4) Shopify analytics meta (if available)
      if (window.ShopifyAnalytics?.meta?.selectedVariant?.title) {
        return window.ShopifyAnalytics.meta.selectedVariant.title;
      }

      // 5) Resolve via selected variant id from analytics/product JSON
      const vid = this.getCurrentVariantId();
      if (vid) {
        const resolved = this.resolveVariantTitleById(vid);
        if (resolved) return resolved;
      }
    } catch (e) {
      console.warn('FBT: unable to read main product variant', e);
    }
    return null;
  }

  getMainVariantSizeToken(title) {
    if (!title) return '';
    let sizeCandidate = title;
    // If title looks like "Product Name - 4T", use the part after the last dash
    if (title.includes(' - ')) {
      sizeCandidate = title.split(' - ').pop().trim();
    }
    
    // Try to match month ranges first (e.g., "6-12 Months", "12-18 Months")
    const monthRangeMatch = sizeCandidate.match(/(\d+[-–—−]\d+\s*m(?:onths?)?)/i);
    if (monthRangeMatch) {
      sizeCandidate = monthRangeMatch[1];
      return this.normalizeSizeToken(sizeCandidate);
    }
    
    // Try to match single month sizes (e.g., "3 Months", "24 Months")
    const singleMonthMatch = sizeCandidate.match(/(\d+\s*m(?:onths?)?)/i);
    if (singleMonthMatch) {
      sizeCandidate = singleMonthMatch[1];
      return this.normalizeSizeToken(sizeCandidate);
    }
    
    // Try to match year sizes (e.g., "2 Years", "3T", "4yr")
    const yearMatch = sizeCandidate.match(/(\d+\s*(?:t|yr|yrs|year|years)?)\b/i);
    if (yearMatch) {
      sizeCandidate = yearMatch[1];
      return this.normalizeSizeToken(sizeCandidate);
    }
    
    // Fallback: just normalize the whole candidate
    return this.normalizeSizeToken(sizeCandidate);
  }

  getCurrentVariantId() {
    try {
      const mainProductSection = document.querySelector('[data-section-id][data-product-id]');
      if (mainProductSection) {
        const select = mainProductSection.querySelector('select[name="id"]');
        if (select && select.value) return select.value;
        const checkedInput = mainProductSection.querySelector('[name="id"][type="radio"]:checked');
        if (checkedInput?.value) return checkedInput.value;
        const hiddenInput = mainProductSection.querySelector('[name="id"][type="hidden"]');
        if (hiddenInput?.value) return hiddenInput.value;
      }
    } catch (e) {
      // ignore
    }
    // Shopify analytics fallback
    if (window.ShopifyAnalytics?.meta?.selectedVariant?.id) {
      return window.ShopifyAnalytics.meta.selectedVariant.id.toString();
    }
    return null;
  }

  getVariantIdFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      const variantId = params.get('variant');
      return variantId ? variantId.toString() : null;
    } catch (e) {
      return null;
    }
  }

  resolveVariantTitleById(variantId) {
    if (!variantId) return '';
    const vid = variantId.toString();
    // Try Shopify analytics product JSON
    const productMeta = window.ShopifyAnalytics?.meta?.product;
    if (productMeta?.variants?.length) {
      const match = productMeta.variants.find(v => v.id.toString() === vid);
      if (match?.name) return match.name;
      if (match?.public_title) return match.public_title;
      if (match?.title) return match.title;
    }
    // Try Product JSON on page
    try {
      const script = document.querySelector('script[type="application/json"][data-product-json],script#ProductJson-product-template,script#product-json');
      if (script?.textContent) {
        const data = JSON.parse(script.textContent);
        if (data?.variants?.length) {
          const match = data.variants.find(v => v.id.toString() === vid);
          if (match?.title) return match.title;
          if (match?.name) return match.name;
        }
      }
    } catch (e) {
      // ignore
    }
    return '';
  }

  resolveVariantTitleFromRendered(variantId) {
    if (!variantId || !this.renderedProducts?.length) return '';
    const vid = variantId.toString();
    for (const product of this.renderedProducts) {
      const match = product.variants?.find(v => v.id?.toString() === vid);
      if (match?.title) return match.title;
    }
    return '';
  }

  getVariantSizeFromId(variantId) {
    if (!variantId) return '';
    const vid = variantId.toString();
    const productMeta = window.ShopifyAnalytics?.meta?.product;
    if (productMeta?.variants?.length) {
      const match = productMeta.variants.find(v => v.id.toString() === vid);
      if (match?.title) return this.getMainVariantSizeToken(match.title);
      if (match?.name) return this.getMainVariantSizeToken(match.name);
    }
    try {
      const script = document.querySelector('script[type="application/json"][data-product-json],script#ProductJson-product-template,script#product-json');
      if (script?.textContent) {
        const data = JSON.parse(script.textContent);
        if (data?.variants?.length) {
          const match = data.variants.find(v => v.id.toString() === vid);
          if (match?.title) return this.getMainVariantSizeToken(match.title);
          if (match?.name) return this.getMainVariantSizeToken(match.name);
        }
      }
    } catch (e) {
      // ignore
    }
    return '';
  }

  syncWithCurrentMainVariant() {
    const vid = this.getCurrentVariantId();
    if (vid) this.mainVariantId = vid;

    if (!this.mainVariantTitle) {
      const resolvedTitle = this.resolveVariantTitleById(vid) || this.resolveVariantTitleFromRendered(vid);
      if (resolvedTitle) {
        this.mainVariantTitle = resolvedTitle;
        this.mainVariantSize = this.getMainVariantSizeToken(resolvedTitle);
      }
    }

    // Fallback: derive size from variant id directly if we still don't have a size
    if (!this.mainVariantSize && (this.mainVariantId || vid)) {
      const resolvedSize = this.getVariantSizeFromId(this.mainVariantId || vid);
      if (resolvedSize) {
        this.mainVariantSize = resolvedSize;
      }
    }

    // Fallback: derive size from rendered products data (main product)
    if (!this.mainVariantSize && this.renderedProducts?.length) {
      const currentProductId = this.container.dataset.currentProductId;
      const mainProduct = this.renderedProducts.find(p => p.id?.toString() === currentProductId?.toString());
      const targetVid = this.mainVariantId || vid;
      if (mainProduct && targetVid && Array.isArray(mainProduct.variants)) {
        const mainVar = mainProduct.variants.find(v => v.id?.toString() === targetVid.toString());
        if (mainVar?.title) {
          this.mainVariantTitle = this.mainVariantTitle || mainVar.title;
          this.mainVariantSize = this.getMainVariantSizeToken(mainVar.title);
        }
        // If still no size, fall back to the first available variant to at least seed a token
        if (!this.mainVariantSize && mainProduct.variants.length) {
          this.mainVariantSize = this.getMainVariantSizeToken(mainProduct.variants[0].title);
          this.mainVariantTitle = this.mainVariantTitle || mainProduct.variants[0].title;
        }
      }
    }

    // Additional fallback: read size text from the rendered main product select in FBT
    if (!this.mainVariantSize) {
      const mainProductId = this.container.dataset.currentProductId;
      const mainSelect = mainProductId
        ? this.container.querySelector(`.fbt-variant-select[data-product-id="${mainProductId}"]`)
        : null;
      if (mainSelect) {
        // Prefer option matching the known variant id
        let optText = '';
        if (this.mainVariantId) {
          const matchOpt = Array.from(mainSelect.options || []).find(o => o.value === this.mainVariantId);
          if (matchOpt) optText = matchOpt.textContent.trim();
        }
        // Otherwise, use the currently selected option text
        if (!optText && mainSelect.options.length && mainSelect.selectedIndex >= 0) {
          optText = mainSelect.options[mainSelect.selectedIndex].textContent.trim();
        }
        if (optText) {
          this.mainVariantTitle = this.mainVariantTitle || optText;
          this.mainVariantSize = this.getMainVariantSizeToken(optText);
        }
      }
    }

    const sizeToken = this.mainVariantSize || this.getMainVariantSizeToken(this.mainVariantTitle);

    // Refresh selects before syncing
    this.variantSelects = this.container.querySelectorAll('.fbt-variant-select');
    if (this.mainVariantTitle || sizeToken || this.mainVariantId) {
      this.updateFbtVariantsToMatch(this.mainVariantTitle, sizeToken, this.mainVariantId);
      // As a safeguard, force-set all selects to the matching size token (if present) even if variant ids differ
      if (sizeToken) {
        this.forceSizeTokenAcrossSelects(sizeToken);
      }
    }
  }

  ensureMainVariantMetadata(products = []) {
    console.log('FBT: ensureMainVariantMetadata called', {
      mainVariantId: this.mainVariantId,
      mainVariantTitle: this.mainVariantTitle,
      mainVariantSize: this.mainVariantSize,
      productsLength: products.length
    });
    
    // Always try to resolve if we have a mainVariantId but no mainVariantSize
    if (!this.mainVariantId) {
      console.log('FBT: ensureMainVariantMetadata - no mainVariantId, returning');
      return;
    }
    
    // If we already have mainVariantSize, skip (but log it)
    if (this.mainVariantSize) {
      console.log('FBT: ensureMainVariantMetadata - mainVariantSize already set:', this.mainVariantSize);
      return;
    }
    
    const pid = this.container.dataset.currentProductId;
    console.log('FBT: Looking for product with ID:', pid);
    
    const main = products.find(p => p.id?.toString() === pid?.toString()) || products[0];
    console.log('FBT: Found main product:', main?.id, main?.title);
    
    if (!main || !Array.isArray(main.variants)) {
      console.log('FBT: ensureMainVariantMetadata - no main product or variants');
      return;
    }
    
    console.log('FBT: Looking for variant with ID:', this.mainVariantId, 'in', main.variants.length, 'variants');
    const match = main.variants.find(v => v.id?.toString() === this.mainVariantId.toString());
    console.log('FBT: Found matching variant:', match?.id, match?.title);
    
    if (match?.title) {
      this.mainVariantTitle = match.title;
      this.mainVariantSize = this.getMainVariantSizeToken(match.title);
      console.log('FBT: Set mainVariantTitle:', this.mainVariantTitle, 'mainVariantSize:', this.mainVariantSize);
    } else {
      console.log('FBT: No matching variant found for ID:', this.mainVariantId);
    }
  }

  forceSizeTokenAcrossSelects(sizeToken) {
    if (!sizeToken) return;
    const normalizedSize = this.normalizeSizeToken(sizeToken);
    const numericSize = this.extractNumericSize(normalizedSize);
    const altSizes = this.buildAltSizeTokens(normalizedSize, numericSize);
    this.variantSelects = this.container.querySelectorAll('.fbt-variant-select');
    this.variantSelects.forEach(select => {
      if (select.tagName !== 'SELECT') return;
      const match = this.findOptionBySize(select, normalizedSize, numericSize, altSizes);
      if (match.option) {
        const prev = select.value;
        select.value = match.option.value;
        select.selectedIndex = match.index;
        Array.from(select.options).forEach((opt, idx) => {
          opt.selected = idx === match.index;
        });
        if (prev !== match.option.value) {
          select.dispatchEvent(new Event('change', { bubbles: true }));
          select.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });
  }
  
  getCurrentProductFromPage() {
    // Try to get current product data from page context
    try {
      // Look for product JSON in script tags
      const productJsonSelectors = [
        'script[type="application/json"][data-product-json]',
        'script[type="application/json"][data-product]',
        'script[data-product-json]',
        'script#ProductJson-product-template',
        'script#product-json'
      ];
      
      for (const selector of productJsonSelectors) {
        const productJson = document.querySelector(selector);
        if (productJson) {
          try {
            const data = JSON.parse(productJson.textContent);
            if (data && data.id) {
              // Convert to our product format
              return {
                id: data.id.toString(),
                handle: data.handle,
                title: data.title,
                vendor: data.vendor || '',
                type: data.type || '',
                price: data.variants?.[0]?.price || 0,
                image: data.featured_image || data.images?.[0] || '',
                images: data.images || [],
                featured_image: data.featured_image || data.images?.[0] || '',
                variants: (data.variants || []).map(v => ({
                  id: v.id.toString(),
                  title: v.title,
                  available: v.available !== false,
                  price: v.price || 0,
                  compare_at_price: v.compare_at_price || 0
                })),
                metafields: {
                  color: data.metafields?.custom?.color || [],
                  product_type_filter: data.metafields?.custom?.product_type_filter || []
                },
                tags: data.tags || []
              };
            }
          } catch (e) {
            console.warn('FBT: Error parsing product JSON', e);
          }
        }
      }
    } catch (error) {
      console.warn('FBT: Error getting current product from page', error);
    }
    return null;
  }

  getCurrentProductFromAnalytics() {
    try {
      const metaProduct = window.ShopifyAnalytics?.meta?.product;
      if (!metaProduct || !metaProduct.id) return null;
      
      // Fallbacks from DOM/data attributes
      const datasetHandle = this.container?.dataset?.currentProductHandle;
      const datasetVendor = this.container?.dataset?.currentVendor;
      const fallbackTitle =
        document.querySelector('h1[data-product-title], h1.product__title, h1.product-title')?.textContent?.trim() ||
        document.querySelector('meta[property="og:title"]')?.getAttribute('content') ||
        document.title ||
        '';
      const ogImage = document.querySelector('meta[property="og:image"]')?.getAttribute('content') || '';
      const selectedVariant = window.ShopifyAnalytics?.meta?.selectedVariant || {};
      
      const handle = metaProduct.handle || datasetHandle || '';
      const title = metaProduct.title || fallbackTitle || '';
      const vendor = metaProduct.vendor || datasetVendor || '';
      const variantsFromMeta = Array.isArray(metaProduct.variants) ? metaProduct.variants : [];
      const images = Array.isArray(metaProduct.images) ? metaProduct.images : [];
      const featuredImage = metaProduct.featured_image || images[0] || ogImage || '';
      const variants =
        variantsFromMeta.length > 0
          ? variantsFromMeta
          : selectedVariant.id
            ? [
                {
                  id: selectedVariant.id.toString(),
                  title: selectedVariant.title || selectedVariant.name || fallbackTitle || 'Default',
                  available: selectedVariant.available !== false,
                  price: selectedVariant.price || 0,
                  compare_at_price: selectedVariant.compare_at_price || 0
                }
              ]
            : [];
      
      return {
        id: metaProduct.id.toString(),
        handle,
        title,
        vendor,
        type: metaProduct.type || '',
        price: variants[0]?.price || metaProduct.variants?.[0]?.price || 0,
        image: featuredImage,
        images,
        featured_image: featuredImage,
        variants: variants.map(v => ({
          id: v.id?.toString(),
          title: v.name || v.public_title || v.title || title || 'Default',
          available: v.available !== false,
          price: v.price || 0,
          compare_at_price: v.compare_at_price || 0
        })),
        metafields: {
          color: metaProduct.metafields?.custom?.color || [],
          product_type_filter: metaProduct.metafields?.custom?.product_type_filter || []
        },
        tags: metaProduct.tags || []
      };
    } catch (error) {
      console.warn('FBT: Error reading product from analytics meta', error);
      return null;
    }
  }
  };
  
  // Initialize all FBT sections when DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    const fbtContainers = document.querySelectorAll('.frequently-bought-together');
    fbtContainers.forEach(container => {
      new window.FrequentlyBoughtTogether(container);
    });
  });
  
  // Add CSS animations (only once)
  if (!document.querySelector('#fbt-styles')) {
    const style = document.createElement('style');
    style.id = 'fbt-styles';
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
      
      .fbt-selected {
        box-shadow: 0 0 0 2px rgba(248, 248, 248, 0.25) !important;
      }
    `;
    document.head.appendChild(style);
  }
  
  // Export for module usage
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = window.FrequentlyBoughtTogether;
  }
}